// Thin fetch wrapper. In dev, Vite proxies these to the backend; in
// production the frontend is served from the same origin so paths work
// as-is.

import { sha256Hex } from "@/features/submit/sha256";
import type { AppConfig, Artifact, BulkSubmitResult, BulkUsage, Event, Job, Me, Node, PresignedURL, SampleIntel, SessionTicket } from "@/types";

const base = ""; // same-origin; dev proxy handles /v1

// Every non-2xx response surfaces as an ApiError. `message` keeps the
// historical "<status> <statusText>: <body text>" format; `status`, the
// parsed `body` (JSON when it parses, else the raw text) and the request
// `path` are there for UI that needs to branch (403 turnstile, 401 login)
// or say "502 from /v1/jobs/…".
export class ApiError extends Error {
  readonly status: number;
  readonly statusText: string;
  readonly body: unknown;
  readonly path: string;

  constructor(message: string, init: { status: number; statusText: string; body: unknown; path: string }) {
    super(message);
    this.name = "ApiError";
    this.status = init.status;
    this.statusText = init.statusText;
    this.body = init.body;
    this.path = init.path;
  }

  /** The backend's `{"error": "…"}` string, or the plain-text body. */
  get detail(): string {
    const b = this.body;
    if (b && typeof b === "object" && typeof (b as { error?: unknown }).error === "string") {
      return (b as { error: string }).error;
    }
    return typeof b === "string" ? b.trim() : "";
  }
}

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError;

function parseBody(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function pathOf(res: Response): string {
  try {
    return new URL(res.url).pathname;
  } catch {
    return res.url;
  }
}

async function apiError(res: Response, message?: (text: string) => string): Promise<ApiError> {
  const text = await res.text().catch(() => res.statusText);
  return new ApiError(message ? message(text) : `${res.status} ${res.statusText}: ${text}`, {
    status: res.status,
    statusText: res.statusText,
    body: parseBody(text),
    path: pathOf(res),
  });
}

async function j<T>(res: Response): Promise<T> {
  if (!res.ok) throw await apiError(res);
  return res.json() as Promise<T>;
}

/** Options for POST /v1/jobs. Turnstile tokens are single-use: pass a fresh one per attempt. */
export interface SubmitOptions {
  turnstileToken?: string;
}

function submitHeaders(opts: SubmitOptions | undefined, extra?: Record<string, string>): Record<string, string> {
  const h: Record<string, string> = { ...extra };
  if (opts?.turnstileToken) h["Cf-Turnstile-Token"] = opts.turnstileToken;
  return h;
}

/** Feature flags when the backend predates GET /v1/config (404): everything off. */
const DEFAULT_CONFIG: AppConfig = {
  auth: { enabled: false, required: false },
  turnstile: { enabled: false },
};

export const api = {
  async nodes(): Promise<{ nodes: Node[] }> {
    return j(await fetch(`${base}/v1/nodes`));
  },

  async jobs(limit = 100): Promise<{ jobs: Job[] }> {
    return j(await fetch(`${base}/v1/jobs?limit=${limit}`));
  },

  async job(id: string): Promise<Job> {
    return j(await fetch(`${base}/v1/jobs/${id}`));
  },

  async submitFile(file: File, spec: SubmitSpec, opts?: SubmitOptions): Promise<Job> {
    const fd = new FormData();
    fd.set("spec", JSON.stringify({ ...spec, kind: "file", filename: file.name }));
    fd.set("file", file);
    return j(await fetch(`${base}/v1/jobs`, { method: "POST", body: fd, headers: submitHeaders(opts) }));
  },

  /** Presigned-S3 upload. Chooses single-PUT or multipart based on size:
   *  files above MULTIPART_THRESHOLD go through the multipart path so no
   *  individual request exceeds Cloudflare's 100 MB request-body cap.
   *
   *  Single-PUT legs: POST /v1/jobs/presign → PUT put_url → POST /v1/jobs/finalize
   *  Multipart legs: POST /v1/jobs/presign-multipart → PUT each part_url
   *                  → POST /v1/jobs/finalize-multipart with {part_number, etag}[]
   *
   *  Turnstile token lives on finalize (both variants); presign doesn't
   *  need one. onProgress fires 0..1 as upload bytes land (summed across
   *  parts in the multipart case).
   */
  async submitFilePresigned(
    file: File,
    sha256: string,
    spec: SubmitSpec,
    opts?: SubmitOptions & { onProgress?: (fraction: number) => void; signal?: AbortSignal },
  ): Promise<Job> {
    // Dedupe short-circuit: if the backend already has this sha in S3
    // (prior run of the same sample), skip the browser PUT entirely —
    // finalize alone creates a new job pointing at the existing bytes.
    // Cheap: one HEAD per submission.
    if (await api.sampleExists(sha256)) {
      opts?.onProgress?.(1);
      const fin = await j<FinalizeResponse>(
        await fetch(`${base}/v1/jobs/finalize`, {
          method: "POST",
          headers: submitHeaders(opts, { "content-type": "application/json" }),
          body: JSON.stringify({ sha256, filename: file.name, spec: { ...spec, kind: "file", filename: file.name } }),
          signal: opts?.signal,
        }),
      );
      return fin.job;
    }
    const MULTIPART_THRESHOLD = 80 * 1024 * 1024; // ≈CF cap minus margin
    if (file.size > MULTIPART_THRESHOLD) {
      return api.submitFilePresignedMultipart(file, sha256, spec, opts);
    }
    const presign = await j<PresignResponse>(
      await fetch(`${base}/v1/jobs/presign`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ sha256, filename: file.name, size: file.size }),
        signal: opts?.signal,
      }),
    );
    await putWithProgress(presign.put_url, file, opts?.onProgress, opts?.signal);
    const fin = await j<FinalizeResponse>(
      await fetch(`${base}/v1/jobs/finalize`, {
        method: "POST",
        headers: submitHeaders(opts, { "content-type": "application/json" }),
        body: JSON.stringify({ sha256, filename: file.name, spec: { ...spec, kind: "file", filename: file.name } }),
        signal: opts?.signal,
      }),
    );
    return fin.job;
  },

  /** Multipart variant of submitFilePresigned. Backend hands back one
   *  presigned PUT per part; we slice the file, upload parts in parallel
   *  (concurrency cap 3 to stay polite + stay under some corporate
   *  outbound rate limits), collect ETags, and call finalize-multipart.
   *  Aborts the upload server-side on error so a half-done upload doesn't
   *  linger until MinIO's lifecycle rule picks it up. */
  async submitFilePresignedMultipart(
    file: File,
    sha256: string,
    spec: SubmitSpec,
    opts?: SubmitOptions & { onProgress?: (fraction: number) => void; signal?: AbortSignal },
  ): Promise<Job> {
    const presign = await j<PresignMultipartResponse>(
      await fetch(`${base}/v1/jobs/presign-multipart`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ sha256, filename: file.name, size: file.size }),
        signal: opts?.signal,
      }),
    );
    const { upload_id, part_urls, part_size } = presign;

    // Per-part progress → total progress.
    const perPart = new Array<number>(part_urls.length).fill(0);
    const emitTotal = () => {
      if (!opts?.onProgress) return;
      const sum = perPart.reduce((a, b) => a + b, 0);
      opts.onProgress(sum / Math.max(1, part_urls.length));
    };

    const etags = new Array<string>(part_urls.length);
    const queue = part_urls.map((url, i) => ({ url, i }));
    const CONC = 3;
    const abortSrv = async () => {
      try {
        await fetch(`${base}/v1/jobs/abort-multipart`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ sha256, upload_id }),
        });
      } catch { /* best-effort; MinIO lifecycle rule is the backstop */ }
    };
    try {
      await Promise.all(
        Array.from({ length: Math.min(CONC, queue.length) }, async () => {
          while (queue.length) {
            const task = queue.shift();
            if (!task) return;
            const start = task.i * part_size;
            const end = Math.min(file.size, start + part_size);
            const blob = file.slice(start, end);
            const etag = await putPartWithProgress(task.url, blob, (f) => { perPart[task.i] = f; emitTotal(); }, opts?.signal);
            etags[task.i] = etag;
          }
        }),
      );
    } catch (e) {
      await abortSrv();
      throw e;
    }

    const parts = etags.map((etag, i) => ({ part_number: i + 1, etag }));
    const fin = await j<FinalizeResponse>(
      await fetch(`${base}/v1/jobs/finalize-multipart`, {
        method: "POST",
        headers: submitHeaders(opts, { "content-type": "application/json" }),
        body: JSON.stringify({ sha256, filename: file.name, upload_id, parts, spec: { ...spec, kind: "file", filename: file.name } }),
        signal: opts?.signal,
      }),
    );
    return fin.job;
  },

  /** Upload an archive via the presigned-S3 path, then ask the backend to
   *  extract it and queue one job per Windows runnable inside. Reuses
   *  submitFilePresigned so a big archive automatically goes through the
   *  multipart path. After the bytes land in S3, calls
   *  /v1/jobs/finalize-archive which does the actual 7z extraction with
   *  the common password list (plus the optional explicit `password`).
   *  Returns the backend's full response: {jobs, dropped, extracted}. */
  async submitArchive(
    file: File,
    sha256: string,
    spec: SubmitSpec,
    opts?: SubmitOptions & { password?: string; onProgress?: (fraction: number) => void; signal?: AbortSignal },
  ): Promise<ArchiveFinalizeResponse> {
    // Dedupe: if the archive is already in S3 we skip the upload leg.
    if (!(await api.sampleExists(sha256))) {
      // Upload the archive once (single-PUT or multipart as needed).
      // submitFilePresigned would try to create a job for it — we don't
      // want that for an archive — so we drive presign + PUT inline.
      const MULTIPART_THRESHOLD = 80 * 1024 * 1024;
      if (file.size > MULTIPART_THRESHOLD) {
        // Big archive — multipart upload only, no finalize-sample.
        const presign = await j<PresignMultipartResponse>(
          await fetch(`${base}/v1/jobs/presign-multipart`, {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ sha256, filename: file.name, size: file.size }),
          }),
        );
        const { upload_id, part_urls, part_size } = presign;
        const perPart = new Array<number>(part_urls.length).fill(0);
        const emit = () => { if (opts?.onProgress) opts.onProgress(perPart.reduce((a, b) => a + b, 0) / Math.max(1, part_urls.length)); };
        const etags = new Array<string>(part_urls.length);
        const queue = part_urls.map((url, i) => ({ url, i }));
        const CONC = 3;
        try {
          await Promise.all(Array.from({ length: Math.min(CONC, queue.length) }, async () => {
            while (queue.length) {
              const task = queue.shift();
              if (!task) return;
              const start = task.i * part_size;
              const end = Math.min(file.size, start + part_size);
              const etag = await putPartWithProgress(task.url, file.slice(start, end), (f) => { perPart[task.i] = f; emit(); }, opts?.signal);
              etags[task.i] = etag;
            }
          }));
        } catch (e) {
          await fetch(`${base}/v1/jobs/abort-multipart`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ sha256, upload_id }) }).catch(() => undefined);
          throw e;
        }
        const parts = etags.map((etag, i) => ({ part_number: i + 1, etag }));
        // Complete the multipart only — no job created for the archive.
        await j<{ ok: true }>(await fetch(`${base}/v1/jobs/complete-multipart`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ sha256, upload_id, parts }),
        }));
      } else {
        const presign = await j<PresignResponse>(
          await fetch(`${base}/v1/jobs/presign`, {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ sha256, filename: file.name, size: file.size }),
          }),
        );
        await putWithProgress(presign.put_url, file, opts?.onProgress, opts?.signal);
      }
    } else {
      opts?.onProgress?.(1);
    }
    // Extract + create jobs.
    return j<ArchiveFinalizeResponse>(
      await fetch(`${base}/v1/jobs/finalize-archive`, {
        method: "POST",
        headers: submitHeaders(opts, { "content-type": "application/json" }),
        body: JSON.stringify({ sha256, filename: file.name, password: opts?.password ?? "", spec: { ...spec, kind: "file", filename: file.name } }),
      }),
    );
  },

  /** HEAD /v1/samples/{sha256} → true if the sample is already in S3.
   *  Used by submitFilePresigned to dedupe when the browser re-uploads a
   *  previously-seen file (same hash) and by the Rerun button on the job
   *  page to skip straight to finalize without a presign+PUT round trip. */
  async sampleExists(sha256: string): Promise<boolean> {
    const r = await fetch(`${base}/v1/samples/${sha256}`, { method: "HEAD" });
    if (r.status === 200) return true;
    if (r.status === 404) return false;
    throw new ApiError(`sampleExists ${r.status}`, { status: r.status, statusText: r.statusText, body: "", path: `/v1/samples/${sha256}` });
  },

  /** Create a new job from an already-uploaded sample identified by its
   *  sha256. Backing endpoint is /v1/jobs/finalize (same path the normal
   *  upload flow lands on); the sample bytes are already in S3 so no PUT
   *  is involved — this is one HTTP round trip. Used by the Rerun button
   *  on a job page. The caller passes the SubmitSpec (profile, network,
   *  timeout, …) — defaults to the old job's spec but can be edited. */
  async rerunSample(sha256: string, filename: string, spec: SubmitSpec, opts?: SubmitOptions): Promise<Job> {
    const fin = await j<FinalizeResponse>(
      await fetch(`${base}/v1/jobs/finalize`, {
        method: "POST",
        headers: submitHeaders(opts, { "content-type": "application/json" }),
        body: JSON.stringify({ sha256, filename, spec: { ...spec, kind: "file", filename } }),
      }),
    );
    return fin.job;
  },

  /** Several samples in one submission. Historically this POSTed one
   *  multipart FormData to /v1/jobs/bulk (backend bulk.go) which could
   *  then unpack archives server-side. Problem: that path goes through
   *  Cloudflare — 100 MB cap per request, WAF signature-scans binaries
   *  → "upload failed" when any file is big OR looks like malware. The
   *  per-file presigned path (submitFilePresigned) already bypasses
   *  CF for the big bytes. We now fan each file through it; the shell
   *  of a BulkSubmitResult is preserved so callers see per-file status
   *  (jobs created vs errors). Archive expansion is still a POST /v1/jobs/bulk
   *  for API callers who upload a zip; the browser never does that
   *  because the user picked discrete files.
   *
   *  Concurrency is capped at 3 to stay polite to the backend's rate
   *  limiter and S3's connection pool. Returns 202-shaped on all-ok,
   *  207 (multi-status) when at least one file fails. */
  async submitBulk(files: readonly File[], spec: SubmitSpec, opts?: SubmitOptions): Promise<BulkSubmitResult> {
    const jobs: NonNullable<BulkSubmitResult["jobs"]> = [];
    const failures: string[] = [];
    const queue = files.slice();
    const CONC = 3;
    const workers = Array.from({ length: Math.min(CONC, queue.length) }, async () => {
      while (queue.length) {
        const file = queue.shift();
        if (!file) return;
        try {
          const bytes = new Uint8Array(await file.arrayBuffer());
          const sha = await sha256Hex(bytes);
          const job = await api.submitFilePresigned(file, sha, spec, opts);
          jobs.push({ id: job.id, filename: file.name, sample_sha256: sha });
        } catch (e) {
          const msg = e instanceof Error ? e.message : String(e);
          failures.push(`${file.name}: ${msg}`);
        }
      }
    });
    await Promise.all(workers);
    return { jobs, ...(failures.length ? { error: failures.join("; ") } : {}) };
  },

  async submitURL(spec: SubmitSpec & { url: string }, opts?: SubmitOptions): Promise<Job> {
    return j(
      await fetch(`${base}/v1/jobs`, {
        method: "POST",
        headers: submitHeaders(opts, { "content-type": "application/json" }),
        body: JSON.stringify({ ...spec, kind: "url" }),
      }),
    );
  },

  /** Public feature flags. A backend without the endpoint (404) means every feature is off. */
  async config(): Promise<AppConfig> {
    const r = await fetch(`${base}/v1/config`);
    if (r.status === 404) return DEFAULT_CONFIG;
    return j(r);
  },

  /** Who is signed in. No auth subsystem (404) reads as signed out. */
  async me(): Promise<Me> {
    const r = await fetch(`${base}/v1/auth/me`);
    if (r.status === 404) return { authenticated: false };
    return j(r);
  },

  async logout(): Promise<void> {
    const r = await fetch(`${base}/v1/auth/logout`, { method: "POST" });
    if (!r.ok) throw await apiError(r);
  },

  /** Update the signed-in viewer's display name. 401 off-session. */
  async updateMe(name: string): Promise<Me> {
    const r = await fetch(`${base}/v1/auth/me`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ name }),
    });
    return j(r);
  },

  /** Signed-in viewer's rolling-24h bulk-submit usage. Returns 401 for signed-out — the hook enables on session only. */
  async bulkUsage(): Promise<BulkUsage> {
    return j(await fetch(`${base}/v1/bulk/usage`));
  },

  /** Full-page login URL; `returnTo` is a path + search on this origin. */
  loginURL(returnTo: string, login = "/v1/auth/github/login"): string {
    return `${base}${login}${login.includes("?") ? "&" : "?"}return=${encodeURIComponent(returnTo)}`;
  },

  /** Short-lived direct S3 URL for an artifact (503 when the store is not S3-backed). */
  /** Live-view ticket. Browser POSTs to pick up a short-lived HS256 JWT
   *  the owning fleet host's /vnc WebSocket verifies. mode=view is
   *  anonymous-allowed; mode=control needs an authed session cookie. */
  async sessionTicket(jobId: string, mode: "view" | "control"): Promise<SessionTicket> {
    return j(
      await fetch(`${base}/v1/jobs/${encodeURIComponent(jobId)}/session-ticket?mode=${mode}`, {
        method: "POST",
        credentials: "include",
      }),
    );
  },

  async presigned(id: string, sha: string, expires?: number): Promise<PresignedURL> {
    const q = expires !== undefined ? `?expires=${Math.round(expires)}` : "";
    return j(await fetch(`${base}/v1/jobs/${id}/artifacts/${sha}/presigned${q}`));
  },

  /** Third-party results for a sample, cached per provider by the backend; force re-asks every provider. Served only
   *  when /v1/config says intel.enabled (a bad hash is a 400). */
  async sampleIntel(sha256: string, opts?: { force?: boolean }): Promise<SampleIntel> {
    return j(await fetch(`${base}/v1/samples/${sha256}/intel${opts?.force ? "?force=1" : ""}`));
  },

  reportURL(id: string): string {
    return `${base}/v1/jobs/${id}/report.html`;
  },

  async artifacts(id: string): Promise<{ artifacts: Artifact[] }> {
    return j(await fetch(`${base}/v1/jobs/${id}/artifacts`));
  },

  artifactURL(id: string, sha: string): string {
    return `${base}/v1/jobs/${id}/artifacts/${sha}`;
  },

  /** A JSON artifact (static-report, behavior-report), always same-origin: never S3 (CORS). */
  async artifactJson<T>(id: string, sha: string): Promise<T> {
    return j(await fetch(`${base}/v1/jobs/${id}/artifacts/${sha}`));
  },

  /** Polling endpoint over the job_events table: returns up to `limit` rows
   *  with offset >= `since`. The backend sets `is_terminal` once the job
   *  reaches a terminal state so the frontend can stop polling. */
  async eventsPoll(id: string, since = 0, limit?: number): Promise<EventsPollResponse> {
    const q = new URLSearchParams({ since: String(since) });
    if (limit !== undefined) q.set("limit", String(limit));
    return j(await fetch(`${base}/v1/jobs/${id}/events?${q.toString()}`));
  },
};

/** One row of the job_events table, as served by GET /v1/jobs/{id}/events.
 *  The `kind` is either a coarse control-plane event (five values below) or
 *  a fine-grained guest-telemetry event prefixed with "telemetry." (e.g.
 *  "telemetry.proc.start", "telemetry.net.flow"). Treat it as a string: the
 *  typed union just names the known coarse set for the editor's benefit. */
export interface JobEventRow {
  offset: number;
  kind:
    | "job_state"
    | "artifact_ready"
    | "verdict_updated"
    | "yara_hit"
    | "config_extracted"
    | (string & {});
  payload?: Record<string, unknown>;
  /** Server-stamped insertion time. Stable across refreshes — use this
   *  for the lifecycle clock, not Date.now(). Optional for older
   *  backends that don't include the field in the response. */
  created_at?: string;
}

/** Shape of the polling endpoint's JSON. */
export interface EventsPollResponse {
  events: JobEventRow[];
  next_offset: number;
  is_terminal: boolean;
}

interface PresignResponse {
  put_url: string;
  s3_key: string;
  expires_at: string;
}

interface PresignMultipartResponse {
  upload_id: string;
  s3_key: string;
  part_urls: string[];
  part_size: number;
  expires_at: string;
}

export interface ArchiveFinalizeResponse {
  extracted: number;
  jobs: { id: string; filename: string; sha256: string }[];
  dropped?: { name: string; reason: string }[];
  error?: string;
}

interface FinalizeResponse {
  job_id: string;
  job: Job;
}

/** XHR-based PUT so a progress event can drive the submit UI's 0..1
 *  indicator. `fetch` doesn't surface upload progress; XHR does. The
 *  Promise rejects on non-2xx with the raw status so the caller can
 *  blame "upload" vs "finalize". */
function putWithProgress(url: string, file: File, onProgress?: (f: number) => void, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    xhr.setRequestHeader("Content-Type", "application/octet-stream");
    xhr.upload.onprogress = (ev) => {
      if (onProgress && ev.lengthComputable) onProgress(ev.loaded / Math.max(1, ev.total));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(1);
        resolve();
      } else {
        reject(new ApiError(`PUT ${xhr.status}: ${xhr.statusText}`, { status: xhr.status, statusText: xhr.statusText, body: xhr.responseText, path: new URL(url).pathname }));
      }
    };
    xhr.onerror = () => reject(new ApiError(`PUT network error`, { status: 0, statusText: "network", body: "", path: new URL(url).pathname }));
    xhr.onabort = () => reject(new DOMException("aborted", "AbortError"));
    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener("abort", () => xhr.abort(), { once: true });
    }
    xhr.send(file);
  });
}

/** Like putWithProgress but for one multipart PART (a File.slice()). Returns
 *  the ETag header S3/MinIO sets on the response — the finalize-multipart
 *  call needs this per part to stitch them back together. ETag quoting is
 *  stripped. If the response header isn't visible (CORS without
 *  Access-Control-Expose-Headers: ETag), we throw a specific error so the
 *  caller can surface a clear fix. */
function putPartWithProgress(url: string, blob: Blob, onProgress?: (f: number) => void, signal?: AbortSignal): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    xhr.setRequestHeader("Content-Type", "application/octet-stream");
    xhr.upload.onprogress = (ev) => {
      if (onProgress && ev.lengthComputable) onProgress(ev.loaded / Math.max(1, ev.total));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(1);
        const raw = xhr.getResponseHeader("ETag") ?? xhr.getResponseHeader("etag");
        if (!raw) {
          reject(new ApiError(
            "PUT ok but ETag response header is not visible — MinIO's CORS needs Access-Control-Expose-Headers: ETag",
            { status: 0, statusText: "etag-hidden", body: "", path: new URL(url).pathname },
          ));
          return;
        }
        resolve(raw.replace(/^"|"$/g, ""));
      } else {
        reject(new ApiError(`PUT ${xhr.status}: ${xhr.statusText}`, { status: xhr.status, statusText: xhr.statusText, body: xhr.responseText, path: new URL(url).pathname }));
      }
    };
    xhr.onerror = () => reject(new ApiError(`PUT network error`, { status: 0, statusText: "network", body: "", path: new URL(url).pathname }));
    xhr.onabort = () => reject(new DOMException("aborted", "AbortError"));
    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener("abort", () => xhr.abort(), { once: true });
    }
    xhr.send(blob);
  });
}

export interface SubmitSpec {
  profile?: string;
  network?: "isolated" | "simulated" | "internet";
  timeout_secs?: number;
  command?: string;
  labels?: string[];
  allow_input?: boolean;
  /** Auto-queue each dropped PE/script as a child job (depth-bounded, deduplicated by SHA-256). */
  auto_analyze_children?: boolean;
  /** Link this submission to an existing job as its child. */
  parent_job_id?: string;
  /** Opt-in: publish this submitter's display name on the resulting job. Default false (anonymous). */
  show_submitter?: boolean;
}

export interface SubmitURLSpec extends SubmitSpec {
  url: string;
}

/** StreamSource is what the stream registry drives. The implementation
 *  used to be an EventSource reading an SSE proxy on the backend; the
 *  backend no longer reaches out to a host for live events, so the
 *  transport is now a Postgres poll with the same abstract shape. */
export type EventStreamSource = {
  close(): void;
  addEventListener(type: "open" | "end", listener: () => void): void;
};

/** openPollingStream polls GET /v1/jobs/{id}/events?since=N on an interval,
 *  translating each job_events row into the frontend's Event shape and
 *  firing `end` when the backend reports is_terminal. The caller
 *  (streamRegistry) owns reconnect/backoff: a failed poll is reported
 *  through onError, mirroring the EventSource "connection failed" path.
 *
 *  The default interval is 2s while the job is active; the function
 *  closes itself the first time it sees is_terminal AND an empty page,
 *  so a terminal job's tail of catch-up events still lands. */
export function openEventStream(
  id: string,
  from: number,
  onEvent: (e: Event) => void,
  onError?: (e: unknown) => void,
  opts?: { intervalMs?: number },
): EventStreamSource {
  const intervalMs = opts?.intervalMs ?? 2000;
  const listeners: Record<"open" | "end", (() => void)[]> = { open: [], end: [] };
  let since = from;
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let opened = false;
  let aborter: AbortController | null = null;

  const fire = (type: "open" | "end") => {
    for (const fn of listeners[type]) fn();
  };

  // Catch-up pages: when the server returns a non-empty response AND
  // isn't terminal-with-empty, we're probably still draining the backlog
  // (server's ListJobEvents caps at 2000 per page). Re-poll immediately
  // in that case instead of sleeping intervalMs — otherwise the Network
  // figure (and every other derived view) re-renders once per page with
  // a visible 2s gap on big jobs. Only sleep intervalMs when we're
  // caught up (events.length === 0) and the job is still live.
  const tick = async () => {
    timer = null;
    if (stopped) return;
    aborter = new AbortController();
    let gotEvents = 0;
    try {
      const r = await api.eventsPoll(id, since);
      aborter = null;
      if (stopped) return;
      if (!opened) {
        opened = true;
        fire("open");
      }
      for (const row of r.events) {
        onEvent(rowToEvent(id, row));
      }
      gotEvents = r.events.length;
      since = r.next_offset;
      if (r.is_terminal && gotEvents === 0) {
        stopped = true;
        fire("end");
        return;
      }
    } catch (err) {
      aborter = null;
      if (stopped) return;
      onError?.(err);
      return; // caller decides whether to retry via streamRegistry backoff
    }
    if (stopped) return;
    const wait = gotEvents > 0 ? 0 : intervalMs;
    timer = setTimeout(() => void tick(), wait);
  };

  // Kick off asynchronously so the caller can wire listeners before "open".
  Promise.resolve().then(() => void tick());

  return {
    close() {
      stopped = true;
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      aborter?.abort();
    },
    addEventListener(type, listener) {
      listeners[type].push(listener);
    },
  };
}

/** Translate one job_events row into the Event shape the frontend already
 *  renders. Two row shapes:
 *
 *   1. Coarse control-plane rows written by the eventbus consumer:
 *        kind = "job_state" | "artifact_ready" | "verdict_updated" |
 *               "yara_hit" | "config_extracted"
 *        payload = whatever the Kafka envelope carried (flat object)
 *
 *   2. Fine-grained guest-telemetry rows written by the events-unpacker:
 *        kind = "telemetry.<kind>"  (e.g. "telemetry.proc.start",
 *               "telemetry.net.flow")
 *        payload = { seq, ts, source, fields }  — the unpacker wraps the
 *        real event under a nested `fields` key.
 *
 *  For (1) spread payload into Event.fields verbatim. For (2) strip the
 *  "telemetry." prefix off the kind, pull ts/source off the payload
 *  wrapper, and use payload.fields as the actual event fields. Previously
 *  every telemetry row fell through mapKind's default to "log", which
 *  broke every per-kind filter and derived counter in the UI. */
function rowToEvent(jobID: string, row: JobEventRow): Event {
  const payload = row.payload ?? {};
  const isTelemetry = typeof row.kind === "string" && row.kind.startsWith("telemetry.");

  if (isTelemetry) {
    const inner = (payload.fields as Record<string, unknown> | undefined) ?? {};
    return {
      job_id: jobID,
      seq: row.offset,
      // Prefer the guest's own event timestamp; fall back to the backend's
      // server-stamped insertion time; last resort now() (never on a fresh
      // backend, only during a staged upgrade).
      ts: typeof payload.ts === "string"
        ? payload.ts
        : row.created_at ?? new Date().toISOString(),
      kind: row.kind.slice("telemetry.".length) as Event["kind"],
      source: (typeof payload.source === "string" ? (payload.source as Event["source"]) : "guest"),
      fields: { ...inner },
    };
  }

  const fields: Record<string, unknown> = { ...payload };
  if (row.kind === "job_state" && typeof payload.state === "string") {
    fields.state = payload.state;
  }
  return {
    job_id: jobID,
    seq: row.offset,
    // Coarse control-plane events don't carry their own ts, so the row's
    // server-stamped created_at is the stable choice. Pre-fix (now()) made
    // the lifecycle clock reset to zero on every refresh because the
    // "running" transition got a fresh timestamp on each poll.
    ts: row.created_at ?? new Date().toISOString(),
    kind: mapKind(row.kind),
    source: "backend",
    fields,
  };
}

function mapKind(k: JobEventRow["kind"]): Event["kind"] {
  switch (k) {
    case "job_state":
      return "job.state";
    case "verdict_updated":
      return "verdict";
    case "artifact_ready":
      return "artifact";
    case "yara_hit":
      return "yara.match";
    case "suricata_alert":
      return "suricata.alert";
    case "config_extracted":
      return "config.extracted";
    default:
      return "log";
  }
}
